import { API } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import {
  TaskActionType,
  TicketPriority,
  TicketStatus,
  TicketTemplate,
  TicketTemplateDefinition,
  TicketTemplateTask,
  orderedTicketPriorities,
  orderedTicketStatuses,
  ticketPriorityLabels,
  ticketStatusLabels
} from "@swivl-mono/types";
import { Checkbox, Input, Modal, ModalSize, TextArea } from "@swivl-mono/ui";
import { useState } from "react";
import { IoIosArrowDown } from "react-icons/io";
import { z } from "zod";

import { CustomerPickerField, SelectedCustomer } from "../../../Components/CustomerPickerModal";
import { TagInput, TagInputType } from "../../../Components/TagInput/TagInput";
import { formatUserDisplayName } from "../../../Helpers/Formatting/formatUserDisplayName";
import { Member } from "../../../Models/Member/Member.Current";

interface TemplateEditorModalProps {
  template: TicketTemplate | null;
  allTemplates: TicketTemplate[];
  onClose: () => void;
  onSave: () => void;
}

const actionTypeConfig: Record<string, { label: string }> = {
  "": { label: "None" },
  [TaskActionType.UPLOAD_FILE]: { label: "Upload File" },
  [TaskActionType.RATING]: { label: "Rating" },
  [TaskActionType.URL]: { label: "URL" },
  [TaskActionType.FREE_TEXT]: { label: "Free Text" }
};

const taskSchema = z.object({
  shortDescription: z.string().trim().min(1, "Task description is required").max(240, "Task description must be 240 characters or less"),
  actionType: z.enum(TaskActionType).nullable().optional()
});

const templateFormSchema = z.object({
  name: z.string().trim().min(1, "Template name is required").max(120, "Template name must be 120 characters or less"),
  title: z.string().max(200, "Default title must be 200 characters or less"),
  description: z.string().max(4000, "Default description must be 4000 characters or less"),
  tags: z.string().max(500, "Tags must be 500 characters or less"),
  tasks: z.array(taskSchema)
});

const nameSchema = templateFormSchema.shape.name;
const titleSchema = templateFormSchema.shape.title;
const descriptionSchema = templateFormSchema.shape.description;
const tagsSchema = templateFormSchema.shape.tags;

export function TemplateEditorModal({ template, allTemplates, onClose, onSave }: TemplateEditorModalProps) {
  const isEditing = !!template;
  const definition = (template?.definition as TicketTemplateDefinition) ?? {};

  const [name, setName] = useState(template?.name ?? "");
  const [enabled, setEnabled] = useState(template?.enabled ?? true);
  const [isDefault, setIsDefault] = useState(template?.isDefault ?? false);
  const [title, setTitle] = useState(definition.predefined?.title ?? "");
  const [description, setDescription] = useState(definition.predefined?.description ?? "");
  const [status, setStatus] = useState<TicketStatus>(definition.predefined?.status ?? TicketStatus.TODO);
  const [priority, setPriority] = useState<TicketPriority>(definition.predefined?.priority ?? TicketPriority.NONE);
  const [tags, setTags] = useState(definition.predefined?.tags?.join(", ") ?? "");
  const [selectedCustomer, setSelectedCustomer] = useState<SelectedCustomer | null>(() => {
    if (typeof definition.predefined?.userId === "number") {
      return { id: definition.predefined.userId, name: template?.resolved?.user?.name ?? formatUserDisplayName(definition.predefined.userId) };
    }
    return null;
  });

  const [tasks, setTasks] = useState<TicketTemplateTask[]>(definition.tasks ?? []);
  const [newTaskDescription, setNewTaskDescription] = useState("");
  const [editingTaskIndex, setEditingTaskIndex] = useState<number | null>(null);
  const [editingTaskText, setEditingTaskText] = useState("");
  const [editingTaskError, setEditingTaskError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const [tagsError, setTagsError] = useState<string | null>(null);
  const [newTaskError, setNewTaskError] = useState<string | null>(null);

  const handleDefaultChange = (checked: boolean): void => {
    if (checked) {
      const existingDefault = allTemplates.find((t) => t.isDefault && t.id !== template?.id);
      const willEnable = !enabled;

      if (willEnable || existingDefault) {
        const messages: string[] = [];
        if (willEnable) {
          messages.push("This template is currently disabled and will be automatically enabled.");
        }
        if (existingDefault) {
          messages.push(`"${existingDefault.name}" is currently the default template and will be replaced.`);
        }
        messages.push("Continue?");

        const confirmed = confirm(messages.join(" "));
        if (!confirmed) return;

        if (willEnable) setEnabled(true);
      }
    }
    setIsDefault(checked);
  };

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddTask = () => {
    const taskResult = taskSchema.safeParse({ shortDescription: newTaskDescription, actionType: null });
    if (!taskResult.success) {
      setNewTaskError(taskResult.error.issues[0]?.message ?? "Invalid task description");
      return;
    }

    setNewTaskError(null);
    setTasks([...tasks, { shortDescription: taskResult.data.shortDescription }]);
    setNewTaskDescription("");
  };

  const handleRemoveTask = (index: number) => {
    setTasks(tasks.filter((_, i) => i !== index));
  };

  const handleUpdateTaskAction = (index: number, actionType: TaskActionType | null) => {
    setTasks(tasks.map((t, i) => (i === index ? { ...t, actionType } : t)));
  };

  const handleStartEditTask = (index: number): void => {
    setEditingTaskIndex(index);
    setEditingTaskText(tasks[index]?.shortDescription ?? "");
    setEditingTaskError(null);
  };

  const handleCancelEditTask = (): void => {
    setEditingTaskIndex(null);
    setEditingTaskText("");
    setEditingTaskError(null);
  };

  const handleSaveEditTask = (): void => {
    if (editingTaskIndex === null) return;
    const result = taskSchema.safeParse({ shortDescription: editingTaskText, actionType: null });
    if (!result.success) {
      setEditingTaskError(result.error.issues[0]?.message ?? "Invalid task description");
      return;
    }
    setTasks(tasks.map((t, i) => (i === editingTaskIndex ? { ...t, shortDescription: result.data.shortDescription } : t)));
    handleCancelEditTask();
  };

  const handleNameBlur = (): void => {
    const result = nameSchema.safeParse(name);
    setNameError(result.success ? null : (result.error.issues[0]?.message ?? "Template name is required"));
  };

  const handleTitleBlur = (): void => {
    const result = titleSchema.safeParse(title);
    setTitleError(result.success ? null : (result.error.issues[0]?.message ?? "Invalid default title"));
  };

  const handleDescriptionBlur = (): void => {
    const result = descriptionSchema.safeParse(description);
    setDescriptionError(result.success ? null : (result.error.issues[0]?.message ?? "Invalid default description"));
  };

  const handleTagsBlur = (): void => {
    const result = tagsSchema.safeParse(tags);
    setTagsError(result.success ? null : (result.error.issues[0]?.message ?? "Invalid tags"));
  };

  const handleNewTaskBlur = (): void => {
    if (!newTaskDescription.trim()) {
      setNewTaskError(null);
      return;
    }

    const result = taskSchema.safeParse({ shortDescription: newTaskDescription, actionType: null });
    setNewTaskError(result.success ? null : (result.error.issues[0]?.message ?? "Invalid task description"));
  };

  const handleSubmit = async () => {
    const validationResult = templateFormSchema.safeParse({
      name,
      title,
      description,
      tags,
      tasks
    });

    if (!validationResult.success) {
      setError(validationResult.error.issues[0]?.message ?? "Please fix the fields with errors and try again");
      setNameError(validationResult.error.issues.find((issue) => issue.path[0] === "name")?.message ?? null);
      setTitleError(validationResult.error.issues.find((issue) => issue.path[0] === "title")?.message ?? null);
      setDescriptionError(validationResult.error.issues.find((issue) => issue.path[0] === "description")?.message ?? null);
      setTagsError(validationResult.error.issues.find((issue) => issue.path[0] === "tags")?.message ?? null);
      return;
    }

    setNameError(null);
    setTitleError(null);
    setDescriptionError(null);
    setTagsError(null);

    setSubmitting(true);
    setError(null);

    const newDefinition: TicketTemplateDefinition = {
      schemaVersion: 1,
      predefined: {
        title: title.trim() || undefined,
        description: description.trim() || undefined,
        status,
        priority,
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        userId: selectedCustomer?.id ?? undefined
      },
      tasks
    };

    try {
      if (isEditing) {
        await API.Ticketing.updateTemplate(Member.orgId, template.id, {
          name: name.trim(),
          enabled,
          isDefault,
          definition: newDefinition
        });
      } else {
        await API.Ticketing.createTemplate(Member.orgId, {
          name: name.trim(),
          enabled,
          isDefault,
          definition: newDefinition
        });
      }
      onSave();
    } catch (e: any) {
      setError(e?.message ?? "Failed to save template");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={(open: boolean) => {
        if (!open) onClose();
      }}
      title={isEditing ? "Edit Template" : "Create Template"}
      size={ModalSize.EXTRA_LARGE}
      className="flex max-h-[90vh] flex-col"
    >
      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>}

        {/* Template name */}
        <div className="mb-4">
          <Input
            label="Template Name"
            required
            error={nameError ?? undefined}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError(null);
            }}
            onBlur={handleNameBlur}
            placeholder="e.g., Bug Report, Feature Request"
          />
        </div>

        {/* Enabled toggle */}
        <div className="mb-4">
          <Checkbox label="Template is enabled" checked={enabled} onCheckedChange={(checked) => setEnabled(checked === true)} />
        </div>

        {/* Default toggle */}
        <div className="mb-6">
          <label className="flex items-center gap-3">
            <Checkbox checked={isDefault} onCheckedChange={(checked) => handleDefaultChange(checked === true)} />
            <div>
              <span className="text-sm text-neutral-700">Set as default template</span>
              <p className="text-xs text-neutral-500">This template will be auto-selected when creating new tickets</p>
            </div>
          </label>
        </div>

        <hr className="mb-6 border-neutral-200" />

        <h3 className="mb-4 text-sm font-semibold text-neutral-900">Pre-filled Values</h3>

        {/* Pre-filled title */}
        <div className="mb-4">
          <TagInput
            value={title}
            label="Default Title"
            error={titleError ?? undefined}
            onSet={(next) => {
              setTitle(next);
              if (titleError) setTitleError(null);
            }}
            onBlur={handleTitleBlur}
            placeholder="Leave empty for no default"
            type={TagInputType.SingleLine}
          />
        </div>

        {/* Pre-filled description */}
        <div className="mb-4">
          <TagInput
            value={description}
            label="Default Description"
            error={descriptionError ?? undefined}
            onSet={(next) => {
              setDescription(next);
              if (descriptionError) setDescriptionError(null);
            }}
            onBlur={handleDescriptionBlur}
            placeholder="Leave empty for no default"
          />
        </div>

        {/* Status and Priority */}
        <div className="mb-4 grid grid-cols-2 gap-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-neutral-700">Default Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as TicketStatus)}
              className="w-full rounded-lg border border-neutral-300 px-4 py-2 text-sm outline-none focus:border-neutral-500"
            >
              {orderedTicketStatuses.map((value) => (
                <option key={value} value={value}>
                  {ticketStatusLabels[value]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-neutral-700">Default Priority</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as TicketPriority)}
              className="w-full rounded-lg border border-neutral-300 px-4 py-2 text-sm outline-none focus:border-neutral-500"
            >
              {orderedTicketPriorities.map((value) => (
                <option key={value} value={value}>
                  {ticketPriorityLabels[value]}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Tags */}
        <div className="mb-4">
          <TagInput
            label="Default Tags"
            value={tags}
            error={tagsError ?? undefined}
            onSet={(next) => {
              setTags(next);
              if (tagsError) setTagsError(null);
            }}
            onBlur={handleTagsBlur}
            placeholder="Comma-separated tags, e.g., urgent, frontend"
            type={TagInputType.SingleLine}
          />
        </div>

        {/* Default Customer */}
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-neutral-700">Default Customer</label>
          <CustomerPickerField
            selectedCustomer={selectedCustomer}
            onSelect={(customer: SelectedCustomer) => {
              setSelectedCustomer(customer);
            }}
            onRemove={() => {
              setSelectedCustomer(null);
            }}
          >
            {({ onClick }) => (
              <button
                type="button"
                onClick={onClick}
                className="flex w-full cursor-pointer items-center rounded-lg border border-neutral-300 px-4 py-2 text-sm outline-none hover:border-neutral-500"
              >
                <span className={filterClasses("flex-1 truncate text-left", selectedCustomer?.name ? "text-black" : "text-neutral-400")}>
                  {selectedCustomer?.name ?? "Select"}
                </span>
                <IoIosArrowDown className="ml-auto size-4 text-neutral-400" />
              </button>
            )}
          </CustomerPickerField>
        </div>

        <hr className="mb-6 border-neutral-200" />

        {/* Tasks */}
        <h3 className="mb-4 text-sm font-semibold text-neutral-900">Default Tasks</h3>

        {/* Task list */}
        <div className="mb-4 space-y-2">
          {tasks.map((task, index) => {
            const isEditing = editingTaskIndex === index;
            return (
              <div
                key={index}
                className={filterClasses(
                  "flex items-center gap-3 rounded-lg border p-3",
                  isEditing ? "border-orange-500 ring-2 ring-orange-100" : "border-neutral-200"
                )}
                onKeyDown={(e) => {
                  if (isEditing && e.key === "Escape") handleCancelEditTask();
                }}
              >
                <div className="min-w-0 flex-1">
                  {isEditing ? (
                    <TagInput
                      value={editingTaskText}
                      onSet={(next) => {
                        setEditingTaskText(next);
                        if (editingTaskError) setEditingTaskError(null);
                      }}
                      onBlur={handleSaveEditTask}
                      onReturnPressed={handleSaveEditTask}
                      placeholder="Task description"
                      error={editingTaskError ?? undefined}
                      type={TagInputType.NoBorder}
                      autoFocus
                    />
                  ) : (
                    <div className="cursor-text text-sm text-neutral-900" role="button" tabIndex={0} onClick={() => handleStartEditTask(index)}>
                      {task.shortDescription}
                    </div>
                  )}
                  {!isEditing && task.actionType && (
                    <div className="mt-1 text-xs text-neutral-500">Action: {actionTypeConfig[task.actionType]?.label}</div>
                  )}
                </div>
                <select
                  value={task.actionType ?? ""}
                  onChange={(e) => handleUpdateTaskAction(index, (e.target.value || null) as TaskActionType | null)}
                  className="rounded border border-neutral-200 px-2 py-1 text-xs outline-none"
                >
                  {Object.entries(actionTypeConfig).map(([value, cfg]) => (
                    <option key={value} value={value}>
                      {cfg.label}
                    </option>
                  ))}
                </select>
                <button onClick={() => handleRemoveTask(index)} className="text-neutral-400 hover:text-red-500" type="button">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>

        {/* Add task */}
        <div className="flex items-start gap-2">
          <TagInput
            value={newTaskDescription}
            onSet={(next) => {
              setNewTaskDescription(next);
              if (newTaskError) setNewTaskError(null);
            }}
            onBlur={handleNewTaskBlur}
            onReturnPressed={handleAddTask}
            placeholder="Add a default task..."
            error={newTaskError ?? undefined}
            type={TagInputType.SingleLine}
            containerClassName="flex-1 min-w-0"
          />
          <button
            onClick={handleAddTask}
            disabled={!newTaskDescription.trim()}
            className="rounded-lg bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-200 disabled:opacity-50"
            type="button"
          >
            Add
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 border-t border-neutral-200 px-6 py-4">
        <button
          onClick={onClose}
          disabled={submitting}
          className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
          type="button"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={
            submitting || nameError !== null || titleError !== null || descriptionError !== null || tagsError !== null || newTaskError !== null
          }
          className="rounded-lg bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 disabled:opacity-50"
          type="button"
        >
          {submitting ? "Saving..." : isEditing ? "Save Changes" : "Create Template"}
        </button>
      </div>
    </Modal>
  );
}
